<template>
  <b-card
    no-body
    class="report-chart"
  >
    <b-card-body>
      <div class="d-flex justify-content-between align-items-start mb-1">
        <div>
          <h4 class="mb-25">
            {{ title }}
          </h4>
          <div
            v-if="subtitle"
            class="report-chart__subtitle"
          >
            {{ subtitle }}
          </div>
        </div>

        <div class="d-flex align-items-center">
          <!-- خلاصه‌ی جمع کل هر متریک، تا عدد بدون تولتیپ هم خوانده شود -->
          <div
            v-for="metric in plotted"
            :key="`total-${metric.Key}`"
            class="report-chart__total"
          >
            <span class="report-chart__total-label">{{ metric.Title }}</span>
            <span class="report-chart__total-value">{{ format(totals[metric.Key], metric.Format) }}</span>
          </div>

          <!-- نمای جدول: معادل تمیزِ نمودار برای خواندن دقیق مقادیر -->
          <b-button
            size="sm"
            :variant="asTable ? 'primary' : 'flat-secondary'"
            class="ml-1"
            :title="asTable ? 'نمایش نمودار' : 'نمایش جدول'"
            @click="asTable = !asTable"
          >
            <feather-icon
              :icon="asTable ? 'BarChart2Icon' : 'ListIcon'"
              size="14"
            />
          </b-button>
        </div>
      </div>

      <!-- در زمان رفرش، رندر قبلی با شفافیت کم نگه داشته می‌شود (بدون پرش) -->
      <div
        class="report-chart__plot"
        :class="{ 'report-chart__plot--busy': loading }"
      >
        <b-table
          v-if="asTable"
          :items="tableItems"
          :fields="tableFields"
          small
          striped
          responsive
          class="report-chart__table mb-0"
        />

        <vue-apex-charts
          v-else-if="hasData"
          :key="chartKey"
          :type="type"
          :height="height"
          :options="chartOptions"
          :series="chartSeries"
        />

        <div
          v-else
          class="report-chart__empty"
          :style="{ height: `${height}px` }"
        >
          <feather-icon
            icon="BarChart2Icon"
            size="28"
          />
          <p class="mt-1 mb-0">
            {{ emptyText }}
          </p>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCardBody } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { baseChartOptions, seriesColor } from './theme'

export default {
  name: 'ReportChart',
  components: { BCardBody, VueApexCharts },
  props: {
    // خروجی ReportModel.TimeSeries از بک‌اند
    series: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    /**
     * کلید متریک‌هایی که رسم می‌شوند. اگر ندهید، همه‌ی متریک‌هایی که
     * هم‌فرمتِ اولی هستند رسم می‌شوند.
     */
    metrics: {
      type: Array,
      default: null,
    },
    type: {
      type: String,
      default: 'line',
    },
    height: {
      type: Number,
      default: 320,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    skin: {
      type: String,
      default: 'light',
    },
    emptyText: {
      type: String,
      default: 'داده‌ای برای این بازه وجود ندارد',
    },
  },
  data() {
    return {
      asTable: false,
    }
  },
  computed: {
    points() {
      return this.series && this.series.Points ? this.series.Points : []
    },

    totals() {
      return (this.series && this.series.Totals) || {}
    },

    hasData() {
      // نمودار خالی بدتر از پیام خالی است
      return this.points.length > 0
        && this.plotted.length > 0
        && this.plotted.some(m => this.points.some(p => (p.Values[m.Key] || 0) !== 0))
    },

    /**
     * فقط متریک‌های هم‌فرمت رسم می‌شوند.
     *
     * دلیل: نمودار دومحوره (دو مقیاس y) همبستگی جعلی می‌سازد و ممنوع است.
     * پس اگر «درآمد» (تومان) و «تعداد سفارش» (عدد) با هم پاس شوند، فقط
     * خانواده‌ی متریک اول رسم می‌شود و بقیه باید نمودار جدا بگیرند.
     */
    plotted() {
      const all = (this.series && this.series.Metrics) || []
      if (!all.length) return []

      const wanted = this.metrics
        ? all.filter(m => this.metrics.includes(m.Key))
        : all

      if (!wanted.length) return []

      const format = wanted[0].Format

      return wanted.filter(m => m.Format === format)
    },

    /** تغییر پوسته یا ترکیب متریک‌ها باید نمودار را از نو بسازد */
    chartKey() {
      return `${this.skin}-${this.type}-${this.plotted.map(m => m.Key).join('|')}-${this.points.length}`
    },

    hasCompare() {
      return this.points.some(p => p.Compare)
    },

    chartSeries() {
      const list = []

      this.plotted.forEach((metric, index) => {
        list.push({
          name: metric.Title,
          data: this.points.map(p => this.round(p.Values[metric.Key] || 0)),
          // اندیس پایدار: رنگ به هویت متریک بسته است نه به رتبه‌اش
          __color: seriesColor(index, this.skin),
        })
      })

      if (this.hasCompare) {
        this.plotted.forEach((metric, index) => {
          list.push({
            name: `${metric.Title} (دوره‌ی قبل)`,
            data: this.points.map(p => this.round((p.Compare && p.Compare[metric.Key]) || 0)),
            __color: seriesColor(index, this.skin),
            __compare: true,
          })
        })
      }

      return list
    },

    chartOptions() {
      const base = baseChartOptions(this.skin)
      const count = this.plotted.length
      const format = this.plotted.length ? this.plotted[0].Format : 'number'

      const options = {
        ...base,
        chart: { ...base.chart, stacked: this.stacked },
        colors: this.chartSeries.map(s => s.__color),
        xaxis: {
          ...base.xaxis,
          categories: this.points.map(p => p.Label),
          // در بازه‌های طولانی برچسب‌ها روی هم می‌افتند
          tickAmount: Math.min(this.points.length, 12),
        },
        yaxis: {
          ...base.yaxis,
          labels: {
            ...base.yaxis.labels,
            formatter: value => this.compact(value, format),
          },
        },
        tooltip: {
          ...base.tooltip,
          y: { formatter: value => this.format(value, format) },
        },
      }

      if (this.type === 'line' || this.type === 'area') {
        options.stroke = {
          curve: 'smooth',
          width: this.chartSeries.map(s => (s.__compare ? 2 : 2)),
          // سری مقایسه خط‌چین است: کدگذاری ثانویه‌ی «همان متریک، دوره‌ی قبل»
          // (خط‌چین روی شبکه ممنوع است، روی سری معنادار مجاز)
          dashArray: this.chartSeries.map(s => (s.__compare ? 5 : 0)),
        }

        options.markers = {
          size: this.points.length <= 20 ? 4 : 0,
          strokeWidth: 2,
          // حلقه‌ی ۲ پیکسلی هم‌رنگ سطح، برای جداکردن نشانگرهای روی‌هم
          strokeColors: this.skin === 'dark' ? '#283046' : '#ffffff',
          hover: { sizeOffset: 2 },
        }

        if (this.type === 'area') {
          options.fill = {
            type: 'gradient',
            gradient: {
              shadeIntensity: 0.5, opacityFrom: 0.28, opacityTo: 0.03, stops: [0, 100],
            },
          }
        }
      }

      if (this.type === 'bar') {
        options.plotOptions = {
          bar: {
            columnWidth: count > 2 ? '70%' : '45%',
            borderRadius: 4,
            // گوشه‌ی گرد فقط انتهای داده، پایه روی خط صفر می‌ماند
            borderRadiusApplication: 'end',
          },
        }
        // فاصله‌ی ۲ پیکسلی هم‌رنگ سطح بین ستون‌های مجاور و سگمنت‌های پشته
        options.stroke = {
          show: true,
          width: 2,
          colors: [this.skin === 'dark' ? '#283046' : '#ffffff'],
        }
      }

      // یک سری نیازی به جعبه‌ی لجند ندارد؛ عنوان کارت خودش نامش را می‌گوید
      options.legend = { ...base.legend, show: this.chartSeries.length > 1 }

      return options
    },

    // ---- نمای جدول ----

    tableFields() {
      const fields = [{ key: 'label', label: 'بازه', thClass: 'text-nowrap' }]

      this.plotted.forEach(metric => {
        fields.push({ key: metric.Key, label: metric.Title, thClass: 'text-nowrap' })

        if (this.hasCompare) {
          fields.push({ key: `${metric.Key}__c`, label: `${metric.Title} (قبل)`, thClass: 'text-nowrap' })
        }
      })

      return fields
    },

    tableItems() {
      return this.points.map(p => {
        const row = { label: p.Label }

        this.plotted.forEach(metric => {
          row[metric.Key] = this.format(p.Values[metric.Key] || 0, metric.Format)

          if (this.hasCompare) {
            row[`${metric.Key}__c`] = this.format((p.Compare && p.Compare[metric.Key]) || 0, metric.Format)
          }
        })

        return row
      })
    },
  },
  methods: {
    round(value, digits = 2) {
      const factor = 10 ** digits
      return Math.round(value * factor) / factor
    },

    group(value) {
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    format(value, formatType) {
      if (value === null || value === undefined) return '—'
      if (formatType === 'percent') return `${this.round(value, 1)}٪`
      if (formatType === 'price') return `${this.group(Math.round(value))} تومان`
      if (formatType === 'duration') return `${this.group(Math.round(value))} ms`
      return this.group(this.round(value))
    },

    /** برچسب محور y باید کوتاه باشد وگرنه پلات را می‌خورد */
    compact(value, formatType) {
      if (value === null || value === undefined) return ''
      if (formatType === 'percent') return `${this.round(value, 1)}٪`

      const abs = Math.abs(value)

      if (abs >= 1000000000) return `${this.round(value / 1000000000, 1)}میلیارد`
      if (abs >= 1000000) return `${this.round(value / 1000000, 1)}م`
      if (abs >= 1000) return `${this.round(value / 1000, 1)}ه`

      return this.group(this.round(value, 1))
    },
  },
}
</script>

<style lang="scss" scoped>
.report-chart {
  height: 100%;

  &__subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  &__total {
    text-align: left;
    margin-right: 1.25rem;
    white-space: nowrap;

    &-label {
      display: block;
      font-size: 0.7rem;
      color: #b9b9c3;
    }

    &-value {
      display: block;
      font-size: 0.95rem;
      font-weight: 600;
    }
  }

  // رفرش نباید اسکلتون فلاش بزند؛ رندر قبلی محو می‌شود و سر جایش می‌ماند
  &__plot {
    transition: opacity 0.15s ease;

    &--busy {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  &__table {
    // جدول پهن باید داخل خودش اسکرول شود، نه اینکه صفحه را افقی کند
    font-size: 0.8rem;

    ::v-deep td,
    ::v-deep th {
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #b9b9c3;
    font-size: 0.85rem;
  }
}
</style>
